import axios from "axios";

import {
  MOCK_ATTEMPTED_KILLS_ENABLED,
  MOCK_ATTEMPTED_KILL_ENTRIES,
} from "../constants/attemptedKills";
import { SERVER_URL } from "../config/config";
import { ProfileNames } from "../types/ProfileName";
import { AttemptedKillEntry } from "../types/AttemptedKillEntry";

const resolveProfileName = (
  address: string,
  profileNames: ProfileNames[]
): string | undefined => {
  const normalized = address.toLowerCase();
  return profileNames.find(
    (profile) => profile.address?.toLowerCase() === normalized
  )?.name;
};

const readOptionalNumber = (
  source: Record<string, unknown>,
  keys: string[]
): number | undefined => {
  for (const key of keys) {
    const raw = source[key];
    if (raw !== undefined && raw !== null && raw !== "") {
      const numeric = Number(raw);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
    }
  }
  return undefined;
};

const normalizeApiEntry = (
  raw: Record<string, unknown>,
  index: number
): AttemptedKillEntry | null => {
  const attackerAddress = String(
    raw.attackerAddress ?? raw.attacker ?? raw.attacker_address ?? ""
  );
  const victimAddress = String(
    raw.victimAddress ?? raw.victim ?? raw.victim_address ?? ""
  );

  if (!attackerAddress || !victimAddress) {
    return null;
  }

  const timestamp = Number(
    raw.timestamp ?? raw.blockTimestamp ?? raw.created_at ?? 0
  );

  return {
    id: String(
      raw.id ?? `api-attempt-${index}-${attackerAddress}-${victimAddress}`
    ),
    timestamp,
    attackerAddress,
    victimAddress,
    attackerName: raw.attackerName
      ? String(raw.attackerName)
      : raw.attacker_name
        ? String(raw.attacker_name)
        : undefined,
    victimName: raw.victimName
      ? String(raw.victimName)
      : raw.victim_name
        ? String(raw.victim_name)
        : undefined,
    bullets: readOptionalNumber(raw, ["bullets"]),
    attackerBullets: readOptionalNumber(raw, [
      "attackerBullets",
      "attacker_bullets",
    ]),
    targetBullets: readOptionalNumber(raw, [
      "targetBullets",
      "target_bullets",
      "victimBullets",
      "victim_bullets",
    ]),
    healthLost: readOptionalNumber(raw, ["healthLost", "health_lost"]),
    attackerHealthLost: readOptionalNumber(raw, [
      "attackerHealthLost",
      "attacker_health_lost",
    ]),
    targetHealthLost: readOptionalNumber(raw, [
      "targetHealthLost",
      "target_health_lost",
      "victimHealthLost",
      "victim_health_lost",
    ]),
    transactionHash: raw.transactionHash
      ? String(raw.transactionHash)
      : raw.transaction_hash
        ? String(raw.transaction_hash)
        : undefined,
    blockNumber:
      raw.blockNumber !== undefined
        ? Number(raw.blockNumber)
        : raw.block_number !== undefined
          ? Number(raw.block_number)
          : undefined,
  };
};

export const fetchAttemptedKillsFromApi = async (): Promise<
  AttemptedKillEntry[]
> => {
  try {
    const response = await axios.get(`${SERVER_URL}/kill/attempts`);
    const payload = response.data as
      | Record<string, unknown>[]
      | { attempts?: Record<string, unknown>[]; data?: Record<string, unknown>[] }
      | unknown;

    const rows: Record<string, unknown>[] = Array.isArray(payload)
      ? payload
      : Array.isArray(
            (payload as { attempts?: Record<string, unknown>[] })?.attempts
          )
        ? (payload as { attempts: Record<string, unknown>[] }).attempts
        : Array.isArray((payload as { data?: Record<string, unknown>[] })?.data)
          ? (payload as { data: Record<string, unknown>[] }).data
          : [];

    return rows
      .map((row, index) => normalizeApiEntry(row, index))
      .filter(
        (entry: AttemptedKillEntry | null): entry is AttemptedKillEntry =>
          entry !== null
      );
  } catch {
    return [];
  }
};

const getMockAttemptedKillEntries = (
  profileNames: ProfileNames[]
): AttemptedKillEntry[] =>
  [...MOCK_ATTEMPTED_KILL_ENTRIES]
    .map((entry) => ({
      ...entry,
      attackerName:
        entry.attackerName ??
        resolveProfileName(entry.attackerAddress, profileNames),
      victimName:
        entry.victimName ??
        resolveProfileName(entry.victimAddress, profileNames),
    }))
    .sort((a, b) => b.timestamp - a.timestamp);

const enrichAttemptedKillEntries = (
  entries: AttemptedKillEntry[],
  profileNames: ProfileNames[]
): AttemptedKillEntry[] =>
  entries
    .map((entry) => ({
      ...entry,
      attackerName:
        entry.attackerName ??
        resolveProfileName(entry.attackerAddress, profileNames),
      victimName:
        entry.victimName ??
        resolveProfileName(entry.victimAddress, profileNames),
    }))
    .sort((a, b) => b.timestamp - a.timestamp);

export const fetchAttemptedKillEntries = async (
  profileNames: ProfileNames[]
): Promise<AttemptedKillEntry[]> => {
  if (MOCK_ATTEMPTED_KILLS_ENABLED) {
    return getMockAttemptedKillEntries(profileNames);
  }

  return enrichAttemptedKillEntries(
    await fetchAttemptedKillsFromApi(),
    profileNames
  );
};
